import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import type { ReactNode } from "react";

import type { AdminActivityLogEntry } from "@/lib/api";

export type SortDir = "asc" | "desc";

interface Props {
  entries: AdminActivityLogEntry[];
  searchTerm: string;
  sortBy: string;
  sortDir: SortDir;
  onSort: (col: string) => void;
  loading?: boolean;
}

/** Wrap every case-insensitive match of `term` in <mark> for search highlight. */
function highlight(text: string, term: string): ReactNode {
  if (!term) {
    return text;
  }
  const lower = text.toLowerCase();
  const q = term.toLowerCase();
  const parts: ReactNode[] = [];
  let i = 0;
  let key = 0;
  for (let next = lower.indexOf(q); next !== -1; next = lower.indexOf(q, i)) {
    parts.push(text.slice(i, next));
    parts.push(
      <mark key={key++} className="bg-accent/30 rounded px-0.5">
        {text.slice(next, next + term.length)}
      </mark>,
    );
    i = next + term.length;
  }
  parts.push(text.slice(i));
  return <>{parts}</>;
}

const COLUMNS: { key: string; label: string; sortable: boolean; right?: boolean }[] = [
  { key: "email", label: "User", sortable: true },
  { key: "action", label: "Action", sortable: true },
  { key: "detail", label: "Detail", sortable: false },
  { key: "created_at", label: "When", sortable: true, right: true },
];

export function ActivityTable({ entries, searchTerm, sortBy, sortDir, onSort, loading }: Props) {
  return (
    <div className="relative overflow-x-auto rounded-lg border">
      {loading && (
        <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center">
          <span className="text-muted-foreground text-xs">Loading…</span>
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-secondary border-b">
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                className={`px-4 py-3 font-medium ${col.right ? "text-right" : "text-left"}`}
              >
                {col.sortable ? (
                  <button
                    type="button"
                    onClick={() => onSort(col.key)}
                    className={`hover:text-primary inline-flex items-center gap-1 ${col.right ? "flex-row-reverse" : ""}`}
                  >
                    {col.label}
                    {sortBy === col.key ? (
                      sortDir === "asc" ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : (
                        <ArrowDown className="h-3 w-3" />
                      )
                    ) : (
                      <ChevronsUpDown className="text-muted-foreground h-3 w-3" />
                    )}
                  </button>
                ) : (
                  col.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 ? (
            <tr>
              <td colSpan={COLUMNS.length} className="text-muted-foreground px-4 py-8 text-center">
                No activity found.
              </td>
            </tr>
          ) : (
            entries.map((entry) => {
              const name = `${entry.first_name ?? ""} ${entry.last_name ?? ""}`.trim();
              return (
                <tr key={entry.id} className="border-b last:border-0">
                  <td className="px-4 py-3">
                    <div className="font-medium">{name ? highlight(name, searchTerm) : "—"}</div>
                    <div className="text-muted-foreground text-xs">
                      {highlight(entry.email ?? entry.user_id, searchTerm)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="bg-secondary text-secondary-foreground inline-block rounded-sm px-2 py-0.5 text-xs font-medium">
                      {highlight(entry.action, searchTerm)}
                    </span>
                  </td>
                  <td className="text-muted-foreground px-4 py-3">
                    {entry.detail ? highlight(entry.detail, searchTerm) : "—"}
                  </td>
                  <td className="text-muted-foreground px-4 py-3 text-right whitespace-nowrap">
                    {new Date(entry.created_at).toLocaleString()}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
